import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { PaxAmountField, ScopeFields } from "./PricingRuleForm";

const blank = {
  name: "", active: true, priority: 1,
  supplier: "all", airline: "all",
  rbd: [], brand: [], flight_number: "",
  travel_date_from: "", travel_date_to: "",
  travel_days_of_week: [], travel_time_from: "", travel_time_to: "",
  adt: { type: "percent", value: 0 },
  chd: { type: "percent", value: 0 },
  inf: { type: "percent", value: 0 },
  per_segment: false,
};

const fmtDateRange = (a, b) => {
  if (!a && !b) return "—";
  const f = (s) => s ? s.split("-").reverse().join("/") : "…";
  return `${f(a)} → ${f(b)}`;
};
const fmtTimeRange = (a, b) => (!a && !b) ? "—" : `${a || "…"} – ${b || "…"}`;
const dowLabel = (arr) => {
  if (!arr || !arr.length) return "—";
  const m = ["", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  return arr.slice().sort((a, b) => a - b).map((v) => m[v]).join(", ");
};
const fmtCreated = (r) => {
  const dt = r.created_at ? new Date(r.created_at) : null;
  const date = dt && !isNaN(dt) ? dt.toLocaleDateString("en-GB") + " " + dt.toLocaleTimeString("en-GB").slice(0, 5) : "—";
  return { date, by: r.created_by || "—" };
};

const amt = (p) => {
  if (!p || !p.value) return "—";
  return p.type === "percent" ? `${p.value}%` : `$${p.value}`;
};

export default function AdminMarkupRules() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editor, setEditor] = useState(null); // {mode: "create"|"edit", rule}

  const load = async () => {
    try {
      const { data } = await api.get("/admin/markup-rules");
      setRows(data || []);
    } catch {
      toast.error("Failed to load markup rules");
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => setEditor({ mode: "create", rule: { ...blank } });
  const openEdit   = (r) => setEditor({ mode: "edit",   rule: { ...blank, ...r } });

  const save = async () => {
    const r = editor.rule;
    if (!r.name?.trim()) { toast.error("Rule name is required"); return; }
    setBusy(true);
    try {
      const payload = { ...r, priority: Number(r.priority) || 0 };
      if (editor.mode === "create") {
        await api.post("/admin/markup-rules", payload);
        toast.success("Markup rule created");
      } else {
        await api.patch(`/admin/markup-rules/${r.id}`, payload);
        toast.success("Markup rule updated");
      }
      setEditor(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this markup rule?")) return;
    await api.delete(`/admin/markup-rules/${id}`);
    toast.success("Deleted");
    load();
  };

  const toggleActive = async (r) => {
    await api.patch(`/admin/markup-rules/${r.id}`, { active: !r.active });
    load();
  };

  return (
    <div className="space-y-4" data-testid="admin-markup-rules">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-slate-500">
          Higher <strong>priority</strong> wins when multiple rules match (evaluated independently per pax type).
        </p>
        <Button onClick={openCreate} data-testid="markup-rule-create-btn"
                className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] rounded-full">
          <Plus className="w-4 h-4 mr-1" /> New markup rule
        </Button>
      </div>

      <Card className="overflow-x-auto">
        <Table className="min-w-[1500px]">
          <TableHeader>
            <TableRow>
              <TableHead className="sticky left-0 bg-white z-10">Name</TableHead>
              <TableHead>Priority</TableHead>
              <TableHead>Supplier</TableHead>
              <TableHead>Airline</TableHead>
              <TableHead>RBD</TableHead>
              <TableHead>Brand</TableHead>
              <TableHead>Flight #</TableHead>
              <TableHead>Travel dates</TableHead>
              <TableHead>Depart time</TableHead>
              <TableHead>Days</TableHead>
              <TableHead className="text-right">ADT</TableHead>
              <TableHead className="text-right">CHD</TableHead>
              <TableHead className="text-right">INF</TableHead>
              <TableHead>Per-seg</TableHead>
              <TableHead>Active</TableHead>
              <TableHead>Created</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => {
              const c = fmtCreated(r);
              return (
                <TableRow key={r.id} data-testid={`markup-rule-row-${r.id}`}>
                  <TableCell className="font-medium sticky left-0 bg-white z-10">{r.name}</TableCell>
                  <TableCell className="font-mono">{r.priority}</TableCell>
                  <TableCell className="text-xs uppercase font-mono">{r.supplier || "all"}</TableCell>
                  <TableCell className="text-xs uppercase font-mono">{r.airline || "all"}</TableCell>
                  <TableCell className="text-xs font-mono">{(r.rbd || []).join(", ") || "—"}</TableCell>
                  <TableCell className="text-xs font-mono">{(r.brand || []).join(", ") || "—"}</TableCell>
                  <TableCell className="text-xs font-mono">{r.flight_number || "—"}</TableCell>
                  <TableCell className="text-xs whitespace-nowrap">{fmtDateRange(r.travel_date_from, r.travel_date_to)}</TableCell>
                  <TableCell className="text-xs whitespace-nowrap">{fmtTimeRange(r.travel_time_from, r.travel_time_to)}</TableCell>
                  <TableCell className="text-xs">{dowLabel(r.travel_days_of_week)}</TableCell>
                  <TableCell className="text-right font-mono">{amt(r.adt)}</TableCell>
                  <TableCell className="text-right font-mono">{amt(r.chd)}</TableCell>
                  <TableCell className="text-right font-mono">{amt(r.inf)}</TableCell>
                  <TableCell>{r.per_segment ? "Yes" : "No"}</TableCell>
                  <TableCell>
                    <Switch checked={!!r.active} onCheckedChange={() => toggleActive(r)}
                            data-testid={`markup-rule-active-${r.id}`} />
                  </TableCell>
                  <TableCell className="text-xs whitespace-nowrap">
                    <div className="text-slate-700 font-mono">{c.date}</div>
                    <div className="text-slate-400">by {c.by}</div>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(r)}
                            data-testid={`markup-rule-edit-${r.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(r.id)}
                            data-testid={`markup-rule-delete-${r.id}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={17} className="text-center text-slate-400 py-10">
                  No markup rules yet — click &quot;New markup rule&quot; to add one.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!editor} onOpenChange={(o) => !o && setEditor(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="markup-rule-dialog">
          <DialogHeader>
            <DialogTitle>{editor?.mode === "edit" ? "Edit" : "New"} markup rule</DialogTitle>
          </DialogHeader>

          {editor && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                  <Label>Rule name</Label>
                  <Input
                    data-testid="markup-rule-name"
                    value={editor.rule.name}
                    onChange={(e) => setEditor({ ...editor, rule: { ...editor.rule, name: e.target.value } })}
                    className="h-10"
                    placeholder="e.g. Standard 5% markup, all suppliers"
                  />
                </div>
                <div>
                  <Label>Priority (higher wins)</Label>
                  <Input
                    data-testid="markup-rule-priority"
                    type="number" className="h-10 font-mono"
                    value={editor.rule.priority}
                    onChange={(e) => setEditor({ ...editor, rule: { ...editor.rule, priority: Number(e.target.value) } })}
                  />
                </div>
              </div>

              <ScopeFields
                idPrefix="markup-rule"
                rule={editor.rule}
                set={(p) => setEditor({ ...editor, rule: { ...editor.rule, ...p } })}
              />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 border-t border-slate-100">
                <PaxAmountField id="markup-rule-adt" label="Adult (ADT)" pax={editor.rule.adt}
                  onChange={(p) => setEditor({ ...editor, rule: { ...editor.rule, adt: p } })} />
                <PaxAmountField id="markup-rule-chd" label="Child (CHD)" pax={editor.rule.chd}
                  onChange={(p) => setEditor({ ...editor, rule: { ...editor.rule, chd: p } })} />
                <PaxAmountField id="markup-rule-inf" label="Infant (INF)" pax={editor.rule.inf}
                  onChange={(p) => setEditor({ ...editor, rule: { ...editor.rule, inf: p } })} />
              </div>

              <div className="flex items-center gap-3">
                <Switch
                  data-testid="markup-rule-active"
                  checked={!!editor.rule.active}
                  onCheckedChange={(v) => setEditor({ ...editor, rule: { ...editor.rule, active: !!v } })}
                />
                <Label className="cursor-pointer"
                       onClick={() => setEditor({ ...editor, rule: { ...editor.rule, active: !editor.rule.active } })}>
                  Active
                </Label>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditor(null)}>Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid="markup-rule-save"
                    className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)]">
              {busy ? "Saving…" : "Save rule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
